import { Icon } from '@dixa/lib-shared-habla';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { useMemo } from 'react';

import { AwayStatus } from 'apis/presence';
import { sortPresence } from 'components/menu/utils';
import { PresenceListSection } from 'components/presence/components/presenceListSection';
import { PresenceSectionSkeleton } from 'components/presence/components/presenceSectionSkeleton';
import { PresenceSectionWarning } from 'components/presence/components/presenceSectionWarning';
import {
  PresenceStateT,
  UserPresence,
} from 'components/presence/reducers/presence';

type LegacyPresenceListSectionsProps = {
  presence: PresenceStateT;
  presenceAsyncStatus: AsyncStatus;
  profile: User;
  awayStatuses: AwayStatus[];
};

const statusMap = { working: 0, away: 1, offline: 2 };

export const LegacyPresenceListSections = ({
  presence,
  presenceAsyncStatus,
  profile,
  awayStatuses,
}: LegacyPresenceListSectionsProps): JSX.Element => {
  const { allPresence } = presence;

  const groupedPresence = useMemo(() => {
    const sortedPresence = sortPresence(allPresence);
    return sortedPresence
      .reduce(
        (pre: UserPresence[][], userPresence: UserPresence) => {
          const {
            presence: { status },
          } = userPresence;
          if (statusMap[status] !== undefined && pre[statusMap[status]]) {
            pre[statusMap[status]].push(userPresence);
          }
          return pre;
        },
        [[], [], []] as UserPresence[][],
      )
      .filter((group) => group.length > 0);
  }, [allPresence]);

  const isLoading = presenceAsyncStatus === 'loading';
  const isEmpty = allPresence.length === 0;

  return isLoading ? (
    <>
      <PresenceSectionSkeleton />
      <PresenceSectionSkeleton />
      <PresenceSectionSkeleton />
    </>
  ) : isEmpty ? (
    <PresenceSectionWarning
      title="There's no one here yet"
      message={
        <>
          You can invite people by clicking on the <Icon name="user-plus" />{' '}
          icon above.
        </>
      }
    />
  ) : (
    <>
      {groupedPresence.map((group, index) => (
        <PresenceListSection
          key={index}
          title={`${group[0] ? group[0].presence.status : ''} (${group.length})`}
          presenceList={group}
          profile={profile}
          ownPresence={presence.presence}
          awayStatuses={awayStatuses}
        />
      ))}
    </>
  );
};
